import React, { useEffect, useMemo, useState } from "react";
import { Card, Loader, EmptyState } from "../../../../ui";
import { getTeacherMappedCourses } from "../../../../Api/apiUtils";
import Home from "./Home";
import StudentList from "./StudentData";
import NotMarkClass from "./NotMarkClass";
import NoShowDetails from "./NoShowDetails";
import WorkingHours from "./workingHours";
import LeaveDetails from "./leaveDetails";
import Calendar from "./Calendar/calendar";
import AeCalendar from "./AeCalendar/ae-calendar";
import QAList from "./qa-list";
import AiClassQA from "./AiClassQA";
import CourseTagging from "./courseTagging";
import SeeQAScore from "./SeeQAScore";
import SeeLdQAScore from "./SeeLdQAScore";
import Community from "./community";
import Slots from "./slots";

interface MainContentProps {
  activeTab: string;
  credentialId: any;
  teacherId: string;
  teacherData: any;
  fetchData: () => Promise<void>;
}

const COURSE_TAG_REQUIRED_TABS = new Set(["calendar", "ae calendar"]);

const CourseTaggingRequiredNotice: React.FC = () => (
  <EmptyState
    title="Tag a course first"
    description="Then you can manage Calendar. Open Course Tagging to map a course to this teacher."
  />
);

const MainContent: React.FC<MainContentProps> = ({
  activeTab,
  credentialId,
  teacherId,
  fetchData,
  teacherData,
}) => {
  const normalizedTab = activeTab.trim().toLowerCase();
  const requiresCourseTagging = COURSE_TAG_REQUIRED_TABS.has(normalizedTab);
  const [courseTagCheck, setCourseTagCheck] = useState({
    loading: false,
    hasMapping: false,
  });

  useEffect(() => {
    if (!teacherId || !requiresCourseTagging) {
      setCourseTagCheck({ loading: false, hasMapping: false });
      return;
    }

    let cancelled = false;
    const loadMappedCourses = async () => {
      setCourseTagCheck({ loading: true, hasMapping: false });
      try {
        const mappedCourses = await getTeacherMappedCourses(teacherId);
        if (cancelled) return;
        setCourseTagCheck({
          loading: false,
          hasMapping: mappedCourses.length > 0,
        });
      } catch (error) {
        console.error("Error checking teacher course mapping:", error);
        if (!cancelled) {
          setCourseTagCheck({ loading: false, hasMapping: false });
        }
      }
    };

    void loadMappedCourses();
    return () => {
      cancelled = true;
    };
  }, [teacherId, requiresCourseTagging, normalizedTab]);

  const courseTagGate = useMemo(() => {
    if (!requiresCourseTagging) return null;
    if (courseTagCheck.loading) {
      return <Loader label="Checking course tagging…" />;
    }
    if (!courseTagCheck.hasMapping) {
      return <CourseTaggingRequiredNotice />;
    }
    return null;
  }, [requiresCourseTagging, courseTagCheck.loading, courseTagCheck.hasMapping]);

  const renderContent = () => {
    if (courseTagGate) return courseTagGate;

    switch (normalizedTab) {
      case "profile":
        return (
          <Home
            credentialId={credentialId}
            teacherId={teacherId}
            fetchData={fetchData}
            teacherDetails={teacherData}
          />
        );
      case "student list":
        return (
          <StudentList activeTab={activeTab} credentialId={credentialId} teacherId={teacherId} />
        );
      case "classes not marked":
        return <NotMarkClass credentialId={credentialId} />;
      case "deduction details":
        return <NoShowDetails teacherId={teacherId} />;
      case "working hours":
        return <WorkingHours teacherId={teacherId} />;
      case "leave details":
        return <LeaveDetails teacherId={teacherId} />;
      case "paid calendar":
        return <Calendar  teacherProfileData={teacherData} teacherId={teacherId} />;
      case "slots":
        return <Slots teacherId={teacherId} teacherProfileData={teacherData} />;
      case "ae calendar":
        return <AeCalendar teacherId={teacherId} teacherDetails={teacherData} />;
      case "qa list":
        return <QAList teacherId={teacherId} />;
      case "ai class qa":
        return <AiClassQA teacherId={teacherId} />;
      case "l&d qa score":
        return <SeeLdQAScore teacherId={teacherId} />;
      case "academic qa score":
        return (
          <SeeQAScore
            teacherUserId={teacherData?.teacherProfileDetails?.userId || ""}
            teacherName={
              `${teacherData?.teacherProfileDetails?.firstName || ""} ${teacherData?.teacherProfileDetails?.lastName || ""}`.trim() ||
              undefined
            }
          />
        );
      case "course tagging":
        return <CourseTagging teacherId={teacherId} />;
      case "community":
        return <Community />;
      default:
        return (
          <Home
            credentialId={credentialId}
            teacherId={teacherId}
            fetchData={fetchData}
            teacherDetails={teacherData}
          />
        );
    }
  };

  return (
    <Card className="teacher-profile__main">{renderContent()}</Card>
  );
};

export default MainContent;
